/**
 * Map ApiError.code to user-friendly strings. Codes follow the Go BE handbook
 * convention (UPPER_SNAKE_CASE). Any code not in the map falls back to
 * err.message verbatim, which is the BE-supplied human message.
 *
 * This is for API errors (thrown by the apiClient response interceptor).
 * Firebase auth errors (login flow) are handled separately in
 * apps/brand/src/utils/errorMessages.ts (formatFirebaseError) — different
 * concern, may move to common later (Tier 2 follow-up).
 */
import { ApiError } from './apiClient';

const FRIENDLY_MESSAGES: Record<string, string> = {
  VALIDATION_ERROR: 'Some fields need attention',
  UNAUTHORIZED: 'Please sign in again',
  FORBIDDEN: "You don't have access to that",
  NOT_FOUND: 'Not found',
  CONFLICT: 'Already exists',
  INVALID_STATUS: "This item's data is locked at its current state",
  INTERNAL_ERROR: 'Something went wrong on our side',
  NETWORK_ERROR: 'Network error — check your connection',
};

export function humanizeApiError(err: ApiError): string {
  const friendly = FRIENDLY_MESSAGES[err.code];
  if (typeof friendly === 'string') return friendly;
  return err.message.length > 0 ? err.message : 'Something went wrong';
}

/**
 * True when an error is an ApiError carrying HTTP 404. The BE deliberately
 * returns one 404 for both "resource doesn't exist" AND "you don't have access"
 * (privacy: a brand can't probe other orgs' campaigns), so callers use this to
 * render an explicit "not available" state instead of a broken empty page.
 */
export function isNotFoundError(error: unknown): boolean {
  return error instanceof ApiError && error.httpStatus === 404;
}
